import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../layout/Header';
import Dashboard from './Dashboard';
import StatsDashboard from './StatsDashboard';
import ImportExport from './ImportExport';
import AccountSettings from '../auth/AccountSettings';
import WishlistDashboard from '../wishlist/WishlistDashboard';
import RandomScentPicker from '../features/RandomScentPicker';
import Button from '../ui/Button';

type DashboardTab = 'collection' | 'wishlist' | 'random-picker' | 'statistics' | 'import-export' | 'settings';

const DashboardLayout: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<DashboardTab>('collection');

  const tabs = [
    { id: 'collection', label: 'Collection', icon: '🏠' },
    { id: 'statistics', label: 'Statistics', icon: '📊' },
    { id: 'wishlist', label: 'Wishlist', icon: '💭' },
    { id: 'random-picker', label: 'Scent Picker', icon: '🎲' },
    { id: 'import-export', label: 'Import/Export', icon: '💾' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Always show header */}
      <Header />
      
      {/* Horizontal Navigation Bar (below header) */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-2 sm:space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as DashboardTab)}
                className={`py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors duration-200 whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="mr-1 sm:mr-2">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {activeTab === 'collection' ? (
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Collection content without header since it's already shown above */}
            <div className="mb-2">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                    Welcome back, {user?.username}!
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400 mb-1">
                    Manage and explore your fragrance collection
                  </p>
                </div>
              </div>
            </div>
            <Dashboard />
          </main>
        ) : (
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {activeTab === 'wishlist' && <WishlistDashboard />}
            {activeTab === 'random-picker' && <RandomScentPicker />}
            {activeTab === 'statistics' && <StatsDashboard />}
            {activeTab === 'import-export' && <ImportExport />}
            {activeTab === 'settings' && <AccountSettings />}
          </main>
        )}
      </motion.div>
    </div>
  );
};

export default DashboardLayout;
